import { ProfileRole, ProfileRoleIconMap, ProfileRoleMap } from "@qendo/profile";
import type { TeamListingRecord } from "@qendo/profile/team";
import { lcfirst } from "@samlevy/string-helpers";
import { router } from "expo-router";
import { Pressable, StyleSheet, View } from "react-native";
import { Badge } from "~/components/feedback/Badge";
import { Avatar } from "~/components/layout/Avatar";
import { Subtitle, Title } from "~/components/layout/text";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items, Padding } from "~/constants/layout";
import { Space } from "~/constants/sizing";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { useUser } from "~/providers/UserProvider";
import { formatApproximateDistanceForHumans } from "~/util/dates";

export type TeamListingItemProps = {
    item: TeamListingRecord;
};

export const TeamListingItem = ({ item }: TeamListingItemProps) => {
    const { colours } = useTheme();
    const { user } = useUser();
    const { profile_id } = useProfile();

    const { user: user_data, name, id, created_at, role, emergency_contact } = item;

    const handlePress = () => {
        router.navigate({
            pathname: "/more/care-team/[team_member_id]/show",
            params: { team_member_id: id },
        });
    };

    const is_share = role === ProfileRole.share;
    const is_owner = role === ProfileRole.owner;

    const subtitle =
        (user_data?.id === user.id && <Badge title="You" variant="neutral" size="sm" />) ||
        (user_data?.profile_id === profile_id && is_owner && <Badge title="Primary user" size="sm" />) ||
        (created_at && `Invited ${lcfirst(formatApproximateDistanceForHumans(created_at, true))}`) ||
        (ProfileRoleMap[role as ProfileRole] ?? "");

    return (
        <Pressable style={[Gap.column_sm, Flex.row, Padding.vertical_xxs, Items.start]} onPress={handlePress}>
            {user_data && !is_share ? (
                <Avatar target={user_data} size={48} />
            ) : (
                <QendoIcon
                    name={ProfileRoleIconMap[role ?? ProfileRole.share]}
                    size={48}
                    color={colours.icon.primary}
                />
            )}
            <View style={styles.body}>
                <Title size="base">{name}</Title>
                <Subtitle>{subtitle}</Subtitle>
            </View>
            {emergency_contact && <Badge title="Emergency Contact" icon="star-of-life" size="sm" variant="danger" />}
        </Pressable>
    );
};

const styles = StyleSheet.create({
    body: {
        flexGrow: 1,
        rowGap: Space.xxx,
        alignItems: "stretch",
    },
});
